import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Target } from "lucide-react";

const categoryData = {
  beans: {
    title: "Beans",
    description: "Premium quality beans sourced from Ethiopia's fertile highlands",
    items: ["Kidney Beans", "Pinto Beans", "Black Beans", "Navy Beans", "Mung Beans", "Faba Beans"]
  },
  pulses: {
    title: "Pulses",
    description: "High-grade pulses processed with advanced cleaning technology",
    items: ["Red Lentils", "Green Lentils", "Chickpeas", "Yellow Peas", "Green Peas", "Split Peas"]
  },
  "oil-seeds": {
    title: "Oil Seeds",
    description: "Nutrient-rich oil seeds from sustainable Ethiopian farms",
    items: ["Sesame Seeds", "Niger Seeds", "Sunflower Seeds", "Flax Seeds", "Safflower Seeds"]
  },
  spices: {
    title: "Spices",
    description: "Aromatic Ethiopian spices with authentic flavors",
    items: ["Black Cumin", "Turmeric", "Coriander", "Fenugreek", "Ginger", "Cardamom"]
  }
};

const CategoryPage = () => {
  const { category } = useParams();
  const data = categoryData[category];
  const [hoveredCard, setHoveredCard] = useState(null);

  if (!data) return <div className="min-h-screen flex items-center justify-center">Category not found</div>;

  return (
    <section className="relative min-h-screen py-24 md:py-32 bg-gray-50">
      <div className="container relative z-10">
        <Link to="/#products">
          <motion.button
            className="flex items-center gap-2 text-brand-navy hover:text-brand-yellow mb-12 transition-colors font-medium"
            whileHover={{ x: -5 }}
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Products
          </motion.button>
        </Link>

        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-navy mb-4">
            {data.title}
            <span className="block text-brand-yellow mt-2">Collection</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">{data.description}</p>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          {data.items.map((item, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              onHoverStart={() => setHoveredCard(index)}
              onHoverEnd={() => setHoveredCard(null)}
            >
              <motion.div
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl border border-gray-100 transition-all duration-300"
                whileHover={{ y: -8 }}
              >
                <div className="w-full h-48 rounded-xl bg-gray-100 flex items-center justify-center mb-4">
                  <span className="text-gray-400 text-sm font-medium">Product Image</span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-brand-yellow/10 text-brand-yellow mb-3">
                  <Target className="w-3 h-3" />
                  Premium Quality
                </div>
                <h3 className="text-lg font-bold text-brand-navy group-hover:text-brand-yellow transition-colors text-center">{item}</h3>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default CategoryPage;
